import React, { use, useCallback, useEffect } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { createPlatformStackNavigator } from "./createPlatformStackNavigator";
import { RootStackParamList } from "./RootNavigation";
import { useCardStyleInterpolator } from "../hooks/use-card-style-interpolator";
import CheckClientInfo from "../screens/auth/CheckClientInfo";
import CheckPrivateClientInfo from "../screens/auth/CheckPrivateClientInfo";
import PhoneNumberScreen from "../screens/auth/PhoneNumber";
import ConfirmationScreen from "../screens/auth/ConfirmationCode";
import SelectRoleScreen from "../screens/auth/SelectRole";
import InvitationCodeScreen from "../screens/auth/InvitationCode";
import CheckWorkerInfo from "../screens/auth/CheckWorkerInfo";
import { useStepsHeader } from "../hooks/use-steps-header";
import { useTracking } from "../hooks/tracking";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import { useI18n } from "../hooks/i18n-hooks";
import { FirebaseManagerContext } from "../contexts/FirebaseManager";
import { InvitationFieldsFragment } from "../__generated__/operations/graphql";

export const signupSigninScreens = [
    { screen: "PhoneNumber", index: 0, titleKey: "screens.auth.phone_number.title" },
    { screen: "ConfirmCode", index: 1, titleKey: "screens.auth.confirmation_code.title" },
    { screen: "SelectRole", index: 2, titleKey: "screens.auth.select_role.title" },
    // An alternative to the role selection (same step), for someone invited by email.
    { screen: "InvitationCode", index: 2, titleKey: "screens.auth.invitation_code.title" },
    { screen: "CheckWorkerInfo", index: 3, titleKey: "screens.auth.check_worker_info.title" },
    { screen: "CheckClientInfo", index: 3, titleKey: "screens.auth.check_client_info.title" },
    {
        screen: "CheckPrivateClientInfo",
        index: 3,
        titleKey: "screens.auth.check_private_client_info.title",
    },
] as const;

export type SignupSigninStackParamList = {
    PhoneNumber: { isSignup?: boolean };
    ConfirmCode: { phoneNumber: string; isSignup: boolean };
    SelectRole?: { isSignup: boolean };
    InvitationCode?: { isSignup?: boolean };
    // `invitationCode`: the invitation was found by the code of its email (see InvitationCode), the
    // signup takes the code instead of the invitation token.
    CheckWorkerInfo?: { invitation: InvitationFieldsFragment; invitationCode?: string };
    CheckClientInfo?: {
        isNewProfile: boolean;
        invitation: InvitationFieldsFragment;
        invitationCode?: string;
    };
    CheckPrivateClientInfo?: { isNewProfile: boolean };
};

export type SignupSigninScreens = keyof SignupSigninStackParamList;

const Stack = createPlatformStackNavigator<SignupSigninStackParamList>();

const StackStepInfo = {
    PhoneNumber: PhoneNumberScreen,
    ConfirmCode: ConfirmationScreen,
    SelectRole: SelectRoleScreen,
    InvitationCode: InvitationCodeScreen,
    CheckWorkerInfo: CheckWorkerInfo,
    CheckClientInfo: CheckClientInfo,
    CheckPrivateClientInfo: CheckPrivateClientInfo,
};

type SignupSigninStackProps = StackScreenProps<RootStackParamList, "SignupSigninStack">;

const SignupSigninStack: React.FC<SignupSigninStackProps> = ({ route, navigation }) => {
    const cardStyleInterpolator = useCardStyleInterpolator();
    const { isSignup = false, initialRouteName = "PhoneNumber" } = route.params || {};
    const { trackEvent } = useTracking();
    const { signOut } = use(FirebaseManagerContext);
    const isLarge = useIsScreenSizeMin("large");
    const i18n = useI18n();
    const getCurrentIndex = useCallback(
        (routeName: keyof SignupSigninStackParamList) => {
            const indexOffset = isLarge ? 2 : 0; // on web we do not have first 2 steps
            const screenIndex = signupSigninScreens.find((r) => r.screen === routeName)?.index;
            if (screenIndex === undefined) {
                return 0; // fallback to 0 if the screen is not found
            }
            return screenIndex - indexOffset;
        },
        [isLarge],
    );

    const getTotalSteps = useCallback(
        (routeName: keyof SignupSigninStackParamList) => {
            // on web we do not have first 2 steps so we always use 2 steps
            if (!isLarge && (isSignup || getCurrentIndex(routeName) > 1)) {
                return 4;
            } else {
                return 2;
            }
        },
        [getCurrentIndex, isSignup, isLarge],
    );

    const { screenListeners, headerComponent } = useStepsHeader<keyof SignupSigninStackParamList>({
        getCurrentIndex,
        getTotalSteps,
        onClose: async () => {
            // we signout the firebase user to allow a new connection with other phone number
            await signOut();
            setTimeout(() => {
                navigation.reset({
                    index: 0,
                    routes: [{ name: "Welcome" }],
                });
            }, 0);
        },
    });

    useEffect(() => {
        if (isSignup) {
            void trackEvent("signup_showed");
        }
    }, [isSignup, trackEvent]);

    return (
        <>
            {headerComponent}
            <Stack.Navigator
                initialRouteName={initialRouteName}
                screenListeners={screenListeners}
                screenOptions={{
                    headerTransparent: false,
                    headerShadowVisible: false,
                    headerBackButtonDisplayMode: "minimal",
                    headerTitleAlign: "center",
                    headerShown: false,
                }}
                webScreenOptions={{
                    headerMode: "float",
                    // Preserve the current web behavior: no card transition (the steps header drives
                    // the animation). Overrides the injected web transition preset. Native uses its
                    // default horizontal slide.
                    cardStyleInterpolator,
                }}
            >
                {signupSigninScreens.map(({ screen, titleKey }) => (
                    <Stack.Screen
                        key={screen}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        name={screen}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        initialParams={{ isSignup }}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={StackStepInfo[screen]}
                        options={{
                            title: i18n.t(titleKey),
                        }}
                    />
                ))}
            </Stack.Navigator>
        </>
    );
};

export default SignupSigninStack;
